import {
  Component,
  Input,
  AfterViewInit,
  ElementRef,
  Self,
  Host,
  ViewChild,
  OnInit,
  HostListener,
  OnDestroy,
} from "@angular/core";
import { ResizedEvent } from "angular-resize-event";
import { fabric } from "fabric";
import { BuildingLayoutService } from "../../../../services/buildingLayout.service";
import { LayoutUnit, UnitPosition, Layout } from "src/app/shared/Models/read";
import * as _ from "lodash";
import { CanvasLayout } from "../../../../models/canvasLayout";
import {
  CanvasMeasurements,
  Size,
  Position,
} from "../../../../models/canvasMeasurements";
import { ILineOptions } from "fabric/fabric-impl";
import { LayoutUnitTypeEnum } from "src/app/shared/Models/read/LayoutUnitType";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

interface IndexedUnit {
  unit: LayoutUnit;
  floorIndex: number;
  unitIndex: number;
}

const gridLineColor = "#DFE1E3";
const gridLineWidth = 1;
const gridLineSectorWidth = 2;

const fillRectColor = "#FFFFFF";
const rectBorderColor = "#c6c6c6";
const rectBorderWidth = 1;
const activeRectBorderColor = "#0E5184";

const activeRectBorderWidth = 2;

const labelFontSize = 18;
const labelFontFamily = "Open Sans";
const labelColor = "#000000";

@Component({
  selector: "app-inside-survey-building-layout-editor-canvas",
  templateUrl: "./layout-editor-canvas.component.html",
  styleUrls: ["./layout-editor-canvas.component.scss"],
})
export class InsideSurveyBuildingLayoutCanvasComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  size: Size;
  scrollPosition: Position;

  @ViewChild("layoutCanvas")
  layoutCanvas: ElementRef<HTMLCanvasElement>;
  layoutCanvasFabric: fabric.StaticCanvas;

  @ViewChild("canvasHoldersContainer")
  canvasHoldersContainer: ElementRef<HTMLDivElement>;

  private selectedUnitPosition: UnitPosition;
  private layout: Layout;

  private canvasProps: {
    layout: CanvasLayout;
    measurements: CanvasMeasurements;
  };

  @Input()
  static: boolean = false;

  constructor(readonly buildingLayoutService: BuildingLayoutService) {}

  ngOnInit(): void {
    if (!this.static)
      this.buildingLayoutService
        .getSelectedUnitPosition()
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.selectedUnitPosition = value;
          this.updateCanvas();
        });

    this.buildingLayoutService
      .getLayout()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.layout = value;
        if (!this.canvasHoldersContainer) return;

        this.size = {
          height: this.canvasHoldersContainer.nativeElement.clientHeight,
          width: this.canvasHoldersContainer.nativeElement.clientWidth,
        };
        this.assignLayout();
        this.redrawCanvas();
      });
  }

  ngAfterViewInit(): void {
    const canvasProps = {
      selection: false,
      renderOnAddRemove: false,
    };
    if (!this.static)
      this.layoutCanvasFabric = new fabric.Canvas(
        this.layoutCanvas.nativeElement,
        canvasProps
      );
    else
      this.layoutCanvasFabric = new fabric.StaticCanvas(
        this.layoutCanvas.nativeElement,
        canvasProps
      );
  }

  assignLayout() {
    if (!this.size) return;

    const canvasLayout = CanvasLayout.create(this.layout);
    const canvasMeasurements = CanvasMeasurements.create(
      canvasLayout,
      this.size,
      gridLineSectorWidth / 2
    );
    this.canvasProps = {
      layout: canvasLayout,
      measurements: canvasMeasurements,
    };

    this.layoutCanvas.nativeElement.style.width = `${this.canvasProps.measurements.size.width}px`;
    this.layoutCanvas.nativeElement.style.height = `${this.canvasProps.measurements.size.height}px`;
    this.layoutCanvas.nativeElement.width =
      this.canvasProps.measurements.size.width;
    this.layoutCanvas.nativeElement.height =
      this.canvasProps.measurements.size.height;
    this.layoutCanvasFabric.setWidth(this.canvasProps.measurements.size.width);
    this.layoutCanvasFabric.setHeight(
      this.canvasProps.measurements.size.height
    );
  }

  redrawCanvas() {
    if (!this.canvasProps) return;

    this.layoutCanvasFabric.clear();

    const lineStyle = {
      fill: gridLineColor,
      stroke: gridLineColor,
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      hasRotatingPoint: false,
      selection: false,
    };

    for (let i = 0; i <= this.canvasProps.layout.lines.x; i++) {
      const p1 = {
        x: i,
        y: 0,
      };
      const p2 = {
        x: i,
        y: this.canvasProps.layout.lines.y,
      };

      const bold = i % 5 === 0;
      const currentLineProps: ILineOptions = {
        ...lineStyle,
        strokeWidth: bold ? gridLineSectorWidth : gridLineWidth,
        selectable: false,
        hasControls: false,
        hasRotatingPoint: false,
      };
      const coords = [
        this.canvasProps.measurements.getX(p1.x),
        this.canvasProps.measurements.getY(p1.y),
        this.canvasProps.measurements.getX(p2.x),
        this.canvasProps.measurements.getY(p2.y),
      ];
      const line = new fabric.Line(coords, currentLineProps);
      this.layoutCanvasFabric.add(line);
    }

    for (let i = 0; i <= this.canvasProps.layout.lines.y; i++) {
      const p1 = {
        x: 0,
        y: i,
      };
      const p2 = {
        x: this.canvasProps.layout.lines.x,
        y: i,
      };

      const bold = i % 5 === 0;
      const currentLineProps = {
        ...lineStyle,
        strokeWidth: bold ? gridLineSectorWidth : gridLineWidth,
        selectable: false,
        hasControls: false,
        hasRotatingPoint: false,
      };

      const coords = [
        this.canvasProps.measurements.getX(p1.x),
        this.canvasProps.measurements.getY(p1.y),
        this.canvasProps.measurements.getX(p2.x),
        this.canvasProps.measurements.getY(p2.y),
      ];
      const line = new fabric.Line(coords, currentLineProps);
      this.layoutCanvasFabric.add(line);
    }

    const rows = this.layout.floors.length;
    const rowHeightCoords =
      (this.canvasProps.layout.lines.y - this.canvasProps.layout.padding.y) /
      rows;
    const rowHeight = this.canvasProps.measurements.getLengthY(rowHeightCoords);

    let selectedGroup: fabric.Group = null;
    for (let i = 0; i < rows; i++) {
      const rowCols = this.layout.floors[i].units.length;
      const cellWidthCoords =
        (this.canvasProps.layout.lines.x - this.canvasProps.layout.padding.x) /
        rowCols;
      const cellWidth =
        this.canvasProps.measurements.getLengthX(cellWidthCoords);

      const y = this.canvasProps.measurements.getY(
        rowHeightCoords * (rows - i - 1) + this.canvasProps.layout.padding.y / 2
      );
      for (let j = 0; j < rowCols; j++) {
        const x = this.canvasProps.measurements.getX(
          cellWidthCoords * j + this.canvasProps.layout.padding.x / 2
        );
        const unitPosition = { floorIndex: i, unitIndex: j };
        const unit = this.layout.getUnit(unitPosition);
        let selected = false;
        if (!this.static)
          selected = this.selectedUnitPosition.sameTo(unitPosition);

        let rectStrokeWidth = rectBorderWidth;
        let strokeRectColor = rectBorderColor;
        if (selected) {
          rectStrokeWidth = activeRectBorderWidth;
          strokeRectColor = activeRectBorderColor;
        }
        const rect = new fabric.Rect({
          left: x,
          top: y,
          height: rowHeight,
          width: cellWidth,
          fill: fillRectColor,
          strokeWidth: rectStrokeWidth,
          stroke: strokeRectColor,
        });
        const data: IndexedUnit = { floorIndex: i, unitIndex: j, unit };
        const group = new fabric.Group([rect], {
          selectable: false,
          moveCursor: "default",
          hoverCursor: "default",
          data,
        });
        const labelText = this.unitLabelText(unit.type);
        if (labelText) {
          const label = new fabric.IText(this.unitLabelText(unit.type), {
            originX: "center",
            originY: "center",
            fontFamily: labelFontFamily,
            fontSize: labelFontSize,
            fill: labelColor,
          });
          group.add(label);
        }

        if (!this.static) {
          group.on("drop", this.onDrop.bind(this));
          group.on("mousedown", this.onMouseDown.bind(this));
          group.on("dragenter", this.onDragEnter.bind(this));
        }

        if (selected) selectedGroup = group;
        else this.layoutCanvasFabric.add(group);
      }
    }
    if (selectedGroup) this.layoutCanvasFabric.add(selectedGroup);

    this.layoutCanvasFabric.requestRenderAll();
  }

  updateCanvas(forceUpdate?: boolean, background?: string) {
    if (!this.layoutCanvasFabric) return;

    this.layoutCanvasFabric.backgroundColor = background;
    const groups = this.layoutCanvasFabric
      .getObjects()
      .filter((x) => x instanceof fabric.Group)
      .map((x) => x as fabric.Group);
    for (const group of groups) {
      const groupUnit = group.data as IndexedUnit;
      const items = group.getObjects();
      const rect = items.find((x) => x instanceof fabric.Rect);
      if (this.selectedUnitPosition.sameTo(groupUnit) && !this.static) {
        group.bringToFront();
        rect.set("strokeWidth", activeRectBorderWidth);
        rect.set("stroke", activeRectBorderColor);
      } else {
        rect.set("strokeWidth", rectBorderWidth);
        rect.set("stroke", rectBorderColor);
      }
    }
    if (forceUpdate) this.layoutCanvasFabric.renderAll();
    else this.layoutCanvasFabric.requestRenderAll();
  }

  unitLabelText(unit: LayoutUnitTypeEnum) {
    switch (unit) {
      case LayoutUnitTypeEnum.serviceUnit:
        return "SU";
      case LayoutUnitTypeEnum.livingUnit:
        return "LU";
      case LayoutUnitTypeEnum.businessUnit:
        return "BU";
      case LayoutUnitTypeEnum.empty:
        return "";
      case LayoutUnitTypeEnum.minusOne:
        return "-1";
    }
  }

  onDrop(e: fabric.IEvent) {
    const { draggingUnitType } = this.buildingLayoutService;
    if (draggingUnitType === undefined) return;

    const thisGroup = e.target as fabric.Group;
    const unit = thisGroup.data as IndexedUnit;
    this.buildingLayoutService.updateSelectedUnit({
      ...unit.unit,
      type: draggingUnitType,
    });
  }

  onMouseDown(e: fabric.IEvent) {
    const thisGroup = e.target as fabric.Group;
    const unit = thisGroup.data as IndexedUnit;
    this.buildingLayoutService.setSelectedUnitPosition(
      UnitPosition.create(unit)
    );
  }

  onDragEnter(e: fabric.IEvent) {
    const group = e.target as fabric.Group;
    const unit = group.data as IndexedUnit;
    this.buildingLayoutService.setSelectedUnitPosition(
      UnitPosition.create(unit)
    );
  }

  onResized(e: ResizedEvent) {
    // 2 times is necessary to handle scrollbar size
    this.size = {
      height: this.canvasHoldersContainer.nativeElement.clientHeight,
      width: this.canvasHoldersContainer.nativeElement.clientWidth,
    };
    this.assignLayout();
    this.size = {
      height: this.canvasHoldersContainer.nativeElement.clientHeight,
      width: this.canvasHoldersContainer.nativeElement.clientWidth,
    };
    this.assignLayout();
    this.redrawCanvas();
  }

  getImage(): string {
    const wasStatic = this.static;
    try {
      if (!wasStatic) {
        this.static = true;
        this.updateCanvas(true, "rgb(237, 238, 239)");
      }
      return this.layoutCanvas.nativeElement.toDataURL();
    } finally {
      this.static = wasStatic;
      if (!wasStatic) this.updateCanvas(true);
    }
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
